import { AgremoDownloadStatus } from '../data-models/agremo-download-status.model';
import { AgremoFileType } from '../data-models/agremo-file-type.model';
import { CreateAnalysisDto } from '../request-models/create-analysis-dto.model';
import { CreateMapDto } from '../request-models/create-map-dto.model';
import { AgremoAnalysisFile } from '../response-models/agremo-analysis-file.model';
import { AgremoAnalysisType } from '../response-models/agremo-analysis-type.model';
import { isRecord } from './is-record';
import { idString, nullableBoolean, nullableString, omitUndefined } from './wire-value-utils';

/** Reads an analysis's `files` array (detail response or complete event). Missing reads as `[]`. */
export function toAgremoAnalysisFiles(value: unknown): AgremoAnalysisFile[] {
  return Array.isArray(value) ? value.filter(isRecord).map(toAgremoAnalysisFile) : [];
}

function toAgremoAnalysisFile(source: Record<string, unknown>): AgremoAnalysisFile {
  return {
    id: idString(source['id']),
    analysisId: idString(source['analysisId']),
    fileType: (nullableString(source['fileType']) ?? '') as AgremoFileType,
    agremoUrl: nullableString(source['agremoUrl']) ?? undefined,
    adnUrl: nullableString(source['adnUrl']) ?? undefined,
    downloadUrl: nullableString(source['downloadUrl']) ?? undefined,
    downloadStatus: (nullableString(source['downloadStatus']) ?? 'pending') as AgremoDownloadStatus,
    downloadedAt: nullableString(source['downloadedAt']) ?? undefined,
  };
}

/** Reads the `/user/agremo/analysis-types` list. Items without an id are dropped. */
export function toAgremoAnalysisTypes(value: unknown): AgremoAnalysisType[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.filter(isRecord).map(toAgremoAnalysisType).filter(type => type.id !== '');
}

function toAgremoAnalysisType(source: Record<string, unknown>): AgremoAnalysisType {
  return {
    id: idString(source['id']),
    name: nullableString(source['name']) ?? '',
    description: nullableString(source['description']) ?? undefined,
    requiresSeedingDensity: nullableBoolean(source['requiresSeedingDensity']) ?? undefined,
  };
}

/** The create-map body (hub reads `fieldId`, `s3Url`, `mapName`, `mapDate`, `cameraType`). Never send an `AgremoMap`. */
export function toCreateMapRequest(dto: CreateMapDto): CreateMapDto {
  return omitUndefined({
    fieldId: dto.fieldId,
    mapName: dto.mapName,
    mapDate: dto.mapDate,
    cameraType: dto.cameraType,
    s3Url: dto.s3Url,
  });
}

/** The create-analysis body, with only the keys that are set. Never send an `AgremoAnalysis`. */
export function toCreateAnalysisRequest(dto: CreateAnalysisDto): CreateAnalysisDto {
  return omitUndefined({
    mapId: dto.mapId,
    analysisTypeId: dto.analysisTypeId,
    cropType: dto.cropType,
    cropVariety: dto.cropVariety,
    growingStage: dto.growingStage,
    seedingDensity: dto.seedingDensity,
    purchaseSessionId: dto.purchaseSessionId,
  });
}
